import { addRupeeCharToFormattedAmount, capitalizeText } from "services";
import { PropertyData } from "./propertyCapture";
import { getSellerTypeDisplay } from "../ShareModal";

export type SharePlatform = "whatsapp" | "email" | "x" | "telegram" | "default";

interface GenerateShareTextOptions {
  platform?: SharePlatform;
  includeLink?: string;
}

const formatBold = (text: string, platform: SharePlatform) => {
  switch (platform) {
    case "whatsapp":
    case "telegram":
      return `*${text}*`; // WhatsApp markdown
    case "email":
      return `<strong>${text}</strong>`; // HTML email
    case "x":
      return text; // X does not support markdown bold
    default:
      return text;
  }
};

const lineBreak = (platform: SharePlatform) => {
  return platform === "email" ? "<br/>" : "\n";
};

export const generatePropertyShareText = (
  property: PropertyData,
  options?: GenerateShareTextOptions,
): string => {
  const platform = options?.platform || "default";
  const br = lineBreak(platform);

  const lines: string[] = [];

  lines.push(`🏠 Check out this property!`, "");

  lines.push(formatBold(capitalizeText(property.title), platform), "");

  const isAuctionProperty = property.emd !== "0" && property.emd !== "";

  if (isAuctionProperty) {
    lines.push(
      `💰 Reserve Price: ${addRupeeCharToFormattedAmount(
        property.reservePrice,
      )}`,
      `📐 Area: ${capitalizeText(property.area)}`,
      `🏷️ Type: ${capitalizeText(property.propertyType)}`,
      `📍 Location: ${capitalizeText(property.location)}`,
      `🏦 Bank: ${capitalizeText(property.bankName)}`,
      `💳 EMD: ${addRupeeCharToFormattedAmount(property.emd)}`,
    );
  } else {
    lines.push(
      `💰 Price: ${addRupeeCharToFormattedAmount(property.reservePrice)}`,
      `📐 Area: ${capitalizeText(property.area)}`,
      `🏷️ Type: ${capitalizeText(property.propertyType)}`,
      `📍 Location: ${capitalizeText(property.location)}`,
    );

    if (property.sellerType) {
      lines.push(`👤 Seller Type: ${capitalizeText(property.sellerType)}`);
    }

    if (property.sellerName) {
      lines.push(`🏷️ Seller Name: ${capitalizeText(property.sellerName)}`);
    }
  }

  if (property.auctionDetails?.length) {
    lines.push(
      "",
      isAuctionProperty ? `📅 Auction Details:` : `📋 Property Details:`,
    );

    const skipFields = [
      "Bank Name",
      "Reserve Price",
      "EMD Amount",
      "Base Price",
    ];

    property.auctionDetails.forEach((detail) => {
      if (!skipFields.includes(detail.label)) {
        lines.push(
          `${capitalizeText(detail.label)}: ${capitalizeText(detail.value)}`,
        );
      }
    });
  }

  if (options?.includeLink) {
    lines.push("", `🔗 View Property: ${options.includeLink}`);
  }

  lines.push("", "Shared from AuctionBazaar.com");

  if (platform === "email") {
    return `
      <div style="font-family: Arial, sans-serif; font-size:14px; line-height:1.6;">
        ${lines.join(br)}
      </div>
    `;
  }

  if (platform === "x") {
    const text = lines.join("\n");
    return text.length > 260 ? text.slice(0, 257) + "..." : text;
  }

  return lines.join(br);
};

interface GenerateMetaDescriptionOptions {
  includeBrand?: boolean;
}

export const generatePropertyMetaDescription = (
  data: any,
  options?: GenerateMetaDescriptionOptions,
): string => {
  const includeBrand = options?.includeBrand ?? true;

  const isAuctionProperty = data?.emd_amount && Number(data?.emd_amount) > 0;

  const reservePrice =
    data?.property_date_type !== "undated"
      ? addRupeeCharToFormattedAmount(data?.reserve_price)
      : "Contact Us";

  const emdAmount =
    data?.property_date_type !== "undated"
      ? addRupeeCharToFormattedAmount(data?.emd_amount)
      : "Contact Us";

  const ownerType =
    data?.owner_type ||
    data?.posted_properties_raw_data?.userType ||
    (isAuctionProperty ? "bank" : "owner");

  const sellerType = capitalizeText(getSellerTypeDisplay(ownerType));

  const locationParts: string[] = [];

  if (data?.city?.name) locationParts.push(capitalizeText(data.city.name));
  if (data?.state?.name) locationParts.push(capitalizeText(data.state.name));

  const location = locationParts.join(", ");

  const propertyType = capitalizeText(
    data?.asset?.name || data?.sub_asset_type_details?.name,
  );

  const title = data?.title
    ? capitalizeText(data.title)
    : `${propertyType || "Property"} In ${location || "India"}`;

  const baseParts: string[] = [];

  if (title) {
    baseParts.push(title);
  }

  const structuredParts: string[] = [];

  if (propertyType) structuredParts.push(propertyType);
  if (location) structuredParts.push(location);

  if (isAuctionProperty) {
    structuredParts.push(`Reserve ${reservePrice}`);
    structuredParts.push(`EMD ${emdAmount}`);
    structuredParts.push("Auction Property");
  } else {
    structuredParts.push(`Price ${reservePrice}`);
  }

  if (sellerType) {
    structuredParts.push(`Seller ${sellerType}`);
  }

  if (structuredParts.length) {
    baseParts.push(structuredParts.join(" | "));
  }

  let description = baseParts.join(". ");

  if (includeBrand) {
    description += " | Listed on AuctionBazaar.com";
  }

  if (description.length > 160) {
    description = description.slice(0, 157).trimEnd() + "...";
  }

  return description;
};
